import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $getSelection,
    $isRangeSelection,
    KEY_ARROW_LEFT_COMMAND,
    KEY_ARROW_RIGHT_COMMAND,
    COMMAND_PRIORITY_LOW,
} from 'lexical';

export default function ArrowKeyNavigationPlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        // Handle right arrow key - focus into node when cursor is at its left
        const unregisterRightArrow = editor.registerCommand(
            KEY_ARROW_RIGHT_COMMAND,
            (event: KeyboardEvent) => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
                    return false;
                }

                const anchor = selection.anchor;
                const anchorNode = anchor.getNode();
                const offset = anchor.offset;

                // First, check if the selection is right before a math node
                // This handles cases where cursor is at the boundary
                let nodeToCheck = null;

                // Case 1: We're at the end of a text node or other inline node
                const textContent = anchorNode.getTextContent();
                if (offset === textContent.length) {
                    nodeToCheck = anchorNode.getNextSibling();
                }

                // Case 2: We're in a paragraph and need to check its children
                if (!nodeToCheck && anchorNode.getType() === 'paragraph') {
                    const children = (anchorNode as any).getChildren?.();
                    if (children && children[offset]) {
                        nodeToCheck = children[offset];
                    }
                }

                if (
                    nodeToCheck &&
                    (nodeToCheck.getType() === 'fraction' ||
                        nodeToCheck.getType() === 'exponent' ||
                        nodeToCheck.getType() === 'root' ||
                        nodeToCheck.getType() === 'overline')
                ) {
                    event?.preventDefault();

                    // Focus the first slot in the math node
                    setTimeout(() => {
                        const dom = editor.getElementByKey(nodeToCheck.getKey());
                        if (dom) {
                            const firstSlot = dom.querySelector<HTMLElement>(
                                '.pm-math-num [contenteditable="true"], ' +
                                    '.pm-math-base [contenteditable="true"], ' +
                                    '.pm-math-index [contenteditable="true"], ' +
                                    '.pm-math-radicand [contenteditable="true"], ' +
                                    '.pm-math-overline [contenteditable="true"]'
                            );
                            firstSlot?.focus();
                        }
                    }, 0);

                    return true;
                }

                return false;
            },
            COMMAND_PRIORITY_LOW
        );

        // Handle left arrow key - focus into node when cursor is at its right
        const unregisterLeftArrow = editor.registerCommand(
            KEY_ARROW_LEFT_COMMAND,
            (event: KeyboardEvent) => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
                    return false;
                }

                const anchor = selection.anchor;
                const anchorNode = anchor.getNode();
                const offset = anchor.offset;

                // First, check if the selection is right after a math node
                let nodeToCheck = null;

                // Case 1: We're at the start of a text node or other inline node
                if (offset === 0) {
                    nodeToCheck = anchorNode.getPreviousSibling();
                }

                // Case 2: We're in a paragraph and need to check its children
                if (!nodeToCheck && anchorNode.getType() === 'paragraph') {
                    const children = (anchorNode as any).getChildren?.();
                    if (children && offset > 0 && children[offset - 1]) {
                        nodeToCheck = children[offset - 1];
                    }
                }

                if (
                    nodeToCheck &&
                    (nodeToCheck.getType() === 'fraction' ||
                        nodeToCheck.getType() === 'exponent' ||
                        nodeToCheck.getType() === 'root' ||
                        nodeToCheck.getType() === 'overline')
                ) {
                    event?.preventDefault();

                    // Focus the last slot in DOM order (visually rightmost)
                    setTimeout(() => {
                        const dom = editor.getElementByKey(nodeToCheck.getKey());
                        if (dom) {
                            const slots = dom.querySelectorAll<HTMLElement>(
                                '[contenteditable="true"]'
                            );
                            if (slots.length > 0) {
                                slots[slots.length - 1]?.focus();
                            }
                        }
                    }, 0);

                    return true;
                }

                return false;
            },
            COMMAND_PRIORITY_LOW
        );

        return () => {
            unregisterRightArrow();
            unregisterLeftArrow();
        };
    }, [editor]);

    return null;
}
